/**
 * Home page showcase: before/after examples + testimonials.
 *
 * HONESTY RULES
 *  - Every entry flagged `illustrative: true` is displayed with an explicit badge
 *    ("AI-generated illustration" / "Sample testimonial"). Never remove the flag
 *    without replacing the content by real, consented material.
 *  - To publish a real anonymized before/after: drop the two JPGs in public/showcase/,
 *    add an entry below with `illustrative: false` and a short consent note in `credit`.
 *  - To publish real testimonials: add entries with `illustrative: false`, first name + age
 *    only (no last names), and keep the text as written by the person.
 */

export const SHOWCASE_EXAMPLES = [
  {
    id: 'example-1',
    before: '/showcase/example-1-before.jpg',
    after: '/showcase/example-1-after.jpg',
    illustrative: true,
    fr: { title: 'Grooming, coupe et posture', text: 'Barbe redessin\u00e9e, coupe textur\u00e9e avec volume, peau hydrat\u00e9e, regard repos\u00e9 et tenue ajust\u00e9e.', axes: ['Esth\u00e9tique faciale', 'Grooming', 'Style'] },
    en: { title: 'Grooming, haircut and posture', text: 'Reshaped beard, textured haircut with volume, hydrated skin, rested gaze and fitted outfit.', axes: ['Facial aesthetics', 'Grooming', 'Style'] },
  },
  {
    id: 'example-2',
    before: '/showcase/example-2-before.jpg',
    after: '/showcase/example-2-after.jpg',
    illustrative: true,
    fr: { title: 'Barbe structur\u00e9e et registre smart casual', text: 'Barbe taill\u00e9e avec ligne de joue nette, mid-fade moderne, peau unifi\u00e9e, veste ajust\u00e9e sur maille sombre.', axes: ['Grooming', 'Style', 'Pr\u00e9sence'] },
    en: { title: 'Structured beard and smart casual register', text: 'Trimmed beard with a sharp cheek line, modern mid-fade, even skin, fitted jacket over dark knit.', axes: ['Grooming', 'Style', 'Presence'] },
  },
];

export const SHOWCASE_TESTIMONIALS = [
  {
    id: 't-1', name: 'Julien', age: 29, illustrative: true,
    fr: { text: 'Le diagnostic m\u2019a donn\u00e9 un ordre de priorit\u00e9 clair : posture et barbe d\u2019abord, le reste ensuite. En six semaines, les retours autour de moi ont chang\u00e9.', focus: 'Posture & grooming' },
    en: { text: 'The diagnosis gave me a clear order of priority: posture and beard first, the rest later. Within six weeks, the feedback around me changed.', focus: 'Posture & grooming' },
  },
  {
    id: 't-2', name: 'Mehdi', age: 34, illustrative: true,
    fr: { text: 'La routine PDF est ce qui m\u2019a fait tenir : cinq minutes le matin, dix le soir. Les rappels email avec un conseil du coach \u00e9vitent de d\u00e9crocher.', focus: 'Routine hebdomadaire' },
    en: { text: 'The PDF routine is what kept me going: five minutes in the morning, ten in the evening. The email reminders with a coach tip prevent dropping out.', focus: 'Weekly routine' },
  },
  {
    id: 't-3', name: 'Thomas', age: 26, illustrative: true,
    fr: { text: 'Voir la simulation avant/apr\u00e8s m\u2019a d\u00e9cid\u00e9 \u00e0 changer de coupe. Le mode comparaison deux mois plus tard montre une vraie \u00e9volution, pas une impression.', focus: 'Simulation & suivi' },
    en: { text: 'Seeing the before/after simulation convinced me to change my haircut. The compare mode two months later shows a real evolution, not an impression.', focus: 'Simulation & tracking' },
  },
];

export const SHOWCASE_COPY = {
  fr: {
    title: 'Avant / Apr\u00e8s et retours d\u2019utilisateurs',
    sub: 'Ce que produit la m\u00e9thode des 5 axes quand elle est appliqu\u00e9e avec constance.',
    illustrativeBadge: 'Exemple illustratif \u2013 image g\u00e9n\u00e9r\u00e9e par IA',
    illustrativeNote: 'Les visuels ci-dessous sont des illustrations g\u00e9n\u00e9r\u00e9es par IA \u00e0 partir de personnes fictives. Ils montrent le type de transformation vis\u00e9e, pas des r\u00e9sultats clients. De vrais avant/apr\u00e8s anonymis\u00e9s, publi\u00e9s avec consentement, les remplaceront progressivement.',
    testimonialsTitle: 'Ils racontent leur progression',
    testimonialBadge: 'Exemple de t\u00e9moignage',
    testimonialsNote: 'T\u00e9moignages illustratifs pr\u00e9sent\u00e9s \u00e0 titre d\u2019exemple ; ils seront remplac\u00e9s par de vrais avis v\u00e9rifi\u00e9s.',
    axesLabel: 'Axes travaill\u00e9s',
    slotTitle: 'Votre avant / apr\u00e8s ici',
    slotText: 'Cet emplacement accueillera de vrais r\u00e9sultats anonymis\u00e9s, publi\u00e9s avec l\u2019accord des personnes. Commencez votre diagnostic pour \u00e9crire le v\u00f4tre.',
    slotCta: 'Commencer mon diagnostic',
    hint: 'Glissez pour comparer',
    beforeAlt: 'Illustration g\u00e9n\u00e9r\u00e9e par IA \u2013 version avant',
    afterAlt: 'Illustration g\u00e9n\u00e9r\u00e9e par IA \u2013 version apr\u00e8s',
    yearsOld: 'ans',
  },
  en: {
    title: 'Before / After and user feedback',
    sub: 'What the 5-axis method produces when applied consistently.',
    illustrativeBadge: 'Illustrative example \u2013 AI-generated image',
    illustrativeNote: 'The visuals below are AI-generated illustrations of fictional people. They show the kind of transformation targeted, not customer results. Real anonymized before/after photos, published with consent, will gradually replace them.',
    testimonialsTitle: 'They share their progress',
    testimonialBadge: 'Sample testimonial',
    testimonialsNote: 'Illustrative testimonials shown as examples; they will be replaced by real verified reviews.',
    axesLabel: 'Axes worked on',
    slotTitle: 'Your before / after here',
    slotText: 'This slot will host real anonymized results, published with the people\u2019s consent. Start your diagnosis to write yours.',
    slotCta: 'Start my diagnosis',
    hint: 'Drag to compare',
    beforeAlt: 'AI-generated illustration \u2013 before version',
    afterAlt: 'AI-generated illustration \u2013 after version',
    yearsOld: 'years old',
  },
};
